<template>
  <div
    v-if="display"
    class="cat-pagination"
    role="group"
    :aria-label="caption"
    :data-label="caption"
  >
    <div class="cat-pagination-results">
      <div
        v-if="!totalUnknown || pagination.displayCount"
        class="pagination-results-item pagination-total-results"
      >
        <span
          >{{ displayTotal }}
          {{
            displayTotal > 1
              ? resolveTxt("skin.results", "results")
              : resolveTxt("skin.result", "result")
          }}</span
        >
      </div>
      <div
        v-if="resultsPerPage && pagination.sizes.length > 1"
        class="pagination-results-item pagination-results-per-page"
      >
        <span class="inline-block">{{
          resolveTxt("pagination.pageSize.resultsPerPage", "Results per page")
        }}</span>
        <inline-menu
          :id="paginationId + '-results-per-page'"
          :value="selectedSize"
          :items="pageSizes"
          :defaultTitle="selectedSize"
          :offscreen-label="
            resolveTxt('pagination.pageSize.resultsPerPage', 'Results per page')
          "
          :menuButtonId="paginationId + '-results-per-page-btn'"
          button
          small
          hollow
          mlt
          @ui-select="pageSize($event, paginationId + '-results-per-page-btn')"
        ></inline-menu>
      </div>
    </div>

    <ul v-if="showPagesNumber" class="cat-pagination-pages right">
      <li v-if="selectedPage != 1">
        <a
          :id="paginationId + '-pageFirst'"
          role="button"
          tabindex="0"
          class="cat-pagination-page button small hollow"
          :aria-label="pageLabel(1)"
          data-label="1"
          @click="pageFirst($event, paginationId + '-pageFirst')"
          @keyup.enter="pageFirst($event, paginationId + '-pageFirst')"
          @keydown.space.prevent="
            pageFirst($event, paginationId + '-pageFirst')
          "
          >1</a
        >
      </li>
      <li v-if="selectedPage == 1 && totalPages > 1">
        <a
          :id="paginationId + '-pageFirst'"
          role="button"
          tabindex="0"
          class="cat-pagination-page selected button small primary"
          aria-disabled="true"
          :aria-label="pageLabel(1)"
          data-label="1"
          >1</a
        >
      </li>

      <li
        v-if="displayPages.length > 0 && displayPages[0] != 2"
        class="pagination-spacer"
      >
        ...
      </li>

      <li v-for="(page, idx) in displayPages" :key="idx">
        <a
          v-if="page != selectedPage"
          :id="paginationId + '-pageSelect'"
          role="button"
          tabindex="0"
          class="cat-pagination-page button small hollow"
          :aria-label="pageLabel(page)"
          :data-label="page"
          @click="pageSelect($event, page, paginationId + '-pageSelect')"
          @keyup.enter="pageSelect($event, page, paginationId + '-pageSelect')"
          @keydown.space.prevent="
            pageSelect($event, page, paginationId + '-pageSelect')
          "
          >{{ page }}</a
        >
        <a
          v-if="page == selectedPage"
          :id="paginationId + '-pageSelect'"
          role="button"
          tabindex="0"
          class="cat-pagination-page selected button small primary"
          aria-disabled="true"
          :aria-label="pageLabel(page)"
          :data-label="page"
          >{{ page }}</a
        >
      </li>

      <li
        v-if="
          displayPages.length > 0 &&
          displayPages[displayPages.length - 1] != totalPages - 1
        "
        class="pagination-spacer"
      >
        ...
      </li>

      <li v-if="selectedPage < totalPages">
        <a
          :id="paginationId + '-pageLast'"
          role="button"
          tabindex="0"
          class="cat-pagination-page button small hollow"
          :aria-label="pageLabel(totalPages)"
          :data-label="totalPages"
          @click="pageLast($event, paginationId + '-pageLast')"
          @keyup.enter="pageLast($event, paginationId + '-pageLast')"
          @keydown.space.prevent="pageLast($event, paginationId + '-pageLast')"
          >{{ totalPages }}</a
        >
      </li>
      <li v-if="selectedPage >= totalPages && totalPages > 1">
        <a
          :id="paginationId + '-pageLast'"
          role="button"
          tabindex="0"
          class="cat-pagination-page selected button small primary"
          aria-disabled="true"
          :aria-label="pageLabel(totalPages)"
          :data-label="totalPages"
          >{{ totalPages }}</a
        >
      </li>
    </ul>
    <div v-if="showPrevNext" class="cat-pagination-pages right">
      <a
        :id="paginationId + '-pagePrev'"
        :aria-disabled="selectedPage === 1 ? 'true' : null"
        :disabled="selectedPage === 1 ? true : null"
        role="button"
        tabindex="0"
        class="pagination-pages-prev button small hollow"
        :aria-label="
          resolveTxt('skin.pagination.page.previous', 'Previous Page')
        "
        :data-label="
          resolveTxt('skin.pagination.page.previous', 'Previous Page')
        "
        @click="pagePrev($event, paginationId + '-pagePrev')"
        @keyup.enter="pagePrev($event, paginationId + '-pagePrev')"
        @keydown.space.prevent="pagePrev($event, paginationId + '-pagePrev')"
        ><span class="icon-angle-left" aria-hidden="false"></span
      ></a>
      <div
        class="pagination-page-items inline-block"
        role="group"
        :aria-label="currentPageCaption"
      >
        <span>{{ resolveTxt("skin.pagination.page", "Page") }}</span>
        <cat-input
          v-if="!totalUnknown"
          :id="paginationId + '-pageSelect'"
          :key="pageInputKey"
          class="fieldset inline-block mlt mrt pbn xtiny dense"
          :value="selectedPageFromInput"
          :label-handled="true"
          :offscreen-label="resolveTxt('skin.pagination.page', 'Page')"
          :data-label="resolveTxt('skin.pagination.page', 'Page')"
          mask="0123456789"
          @ui-change="
            pageSelect($event, $event.value, paginationId + '-pageSelect')
          "
        >
        </cat-input>
        <span v-else class="plx">{{ selectedPageFromInput }}</span>
        <span v-if="!totalUnknown"
          >{{ resolveTxt("skin.pagination.page.of", "of") }}
          {{ totalPages }}</span
        >
      </div>
      <a
        :id="paginationId + '-pageNext'"
        :aria-disabled="
          hasNext === false || (!totalUnknown && selectedPage === totalPages)
            ? 'true'
            : null
        "
        :disabled="
          hasNext === false || (!totalUnknown && selectedPage === totalPages)
            ? true
            : null
        "
        role="button"
        tabindex="0"
        class="pagination-pages-next button small hollow"
        :aria-label="resolveTxt('skin.pagination.page.next', 'Next Page')"
        :data-label="resolveTxt('skin.pagination.page.next', 'Next Page')"
        @click="pageNext($event, paginationId + '-pageNext')"
        @keyup.enter="pageNext($event, paginationId + '-pageNext')"
        @keydown.space.prevent="pageNext($event, paginationId + '-pageNext')"
        ><span class="icon-angle-right" aria-hidden="false"></span
      ></a>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import { defaultStopProcessUpdate } from "@/components/utilities/processUpdateMixin";
import InlineMenu from "@/components/catMenu/catMenu";
import CatInput from "@/components/formelements/cat-input.vue";

let Catalyst = (window as any).Catalyst || {};
export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const stopProcessUpdate = inject(
      "stopProcessUpdate",
      () => defaultStopProcessUpdate
    );

    return {
      resolveTxt,
      stopProcessUpdate
    };
  },
  name: "CatPagination",
  components: {
    InlineMenu,
    CatInput
  },
  props: {
    id: {
      type: [String, Number],
      description:
        "View node id where to apply the pagination commands. This is usually the repeater node id."
    },
    pagingId: {
      type: [String, Number],
      default: null,
      description:
        'Unique id for this component to be used as a prefix for the id\'s of the various elements in this component. If not defined, fallback to the "id" props.'
    },
    paging: {
      type: Boolean,
      default: false
    },
    showPages: {
      type: Boolean,
      default: false
    },
    caption: {
      type: String,
      default: "Pagination",
      description:
        "Accessible name to identify the pagination group. Useful if there are top and bottom pagination in the screen."
    },
    pagination: {
      type: Object,
      default: function () {
        return {
          size: 1,
          page: 1,
          total: 1,
          maxTotalToShowPaging: 0,
          hasNext: null,
          sizes: [1]
        };
      }
    },
    resultsPerPage: {
      type: Boolean,
      default: true,
      description:
        "Displays the results per page. Set to false to not display the results per page."
    }
  },
  data: function () {
    return {
      pageInputKey: this.pagination.page,
      selectedPageFromInput: this.pagination.page
    };
  },
  watch: {
    pagination: function (value) {
      this.selectedPageFromInput = value.page;
      this.pageInputKey = value.page;
    }
  },
  computed: {
    /**
     * Show pages with number when
     * - showPages is true
     * - totalUnknown - Total number of results is available
     */
    showPagesNumber: function () {
      return this.paging && this.showPages && !this.totalUnknown;
    },
    /**
     * Show Prev and Next button when
     * - showPages is false and total pages is more than 1
     * - OR totalUnknown - Total number of results is not available
     */
    showPrevNext: function () {
      return (
        this.paging &&
        ((!this.showPages && this.totalPages > 1) ||
          this.totalUnknown ||
          this.hasNext)
      );
    },
    paginationId: function () {
      return this.pagingId || this.id;
    },
    currentPageCaption: function () {
      return (
        this.resolveTxt("skin.pagination.page", "Page") +
        " " +
        this.selectedPageFromInput +
        " " +
        this.resolveTxt("skin.pagination.page.of", "of") +
        " " +
        this.totalPages
      );
    },
    /**
     * Display - display pagination component
     */
    display: function () {
      if (this.totalUnknown) {
        return true;
      } else {
        let maxTotalToShowPaging = parseInt(
          this.pagination.maxTotalToShowPaging,
          10
        );
        let isHidePagination = false;

        if (this.totalPages == 1) {
          // Get lower value between size and hideTotalSize
          maxTotalToShowPaging = Math.min(
            this.pagination.size,
            maxTotalToShowPaging
          );

          // Checks maxToHide is not the default value 0
          // And if maxTotalToShowPaging is greater than totalAvailable
          isHidePagination =
            maxTotalToShowPaging > 0 &&
            maxTotalToShowPaging >= this.pagination.totalAvailable;
        }

        return this.pagination.total > 0 && !isHidePagination;
      }
    },
    selectedPage: function () {
      return this.pagination.page;
    },
    displayPages: function () {
      var pages = [];
      var totalPages = Math.ceil(
        this.pagination.totalAvailable / this.pagination.size
      );
      var maxPages = 5;

      // Changes pages number to 3 on mobile to keep in a single line
      // Only works on refresh not when resizing the browser
      if (window.innerWidth < 767) {
        maxPages = 3;
      }

      var pagesCurrent = this.pagination.page;
      var pagesStart = 2;
      var pagesEnd = totalPages - 1;

      while (
        pagesCurrent - pagesStart > 2 &&
        totalPages - pagesStart > maxPages
      ) {
        pagesStart++;
      }

      var shownPages = 0;

      for (var p = pagesStart; p <= pagesEnd && shownPages < maxPages; p++) {
        pages.push(p);
        shownPages++;
      }

      return pages;
    },
    pageStart: function () {
      return (this.pagination.page - 1) * this.pagination.size + 1;
    },
    pageEnd: function () {
      var end =
        (this.pagination.page - 1) * this.pagination.size +
        this.pagination.size;
      if (end > this.pagination.totalAvailable) {
        return this.pagination.totalAvailable;
      }
      return end;
    },
    /**
     * Total unkown is when the total number of results won't be available (undefined or null)
     * - Displays pagination arrows only.
     * - Hides Total number of results, and pages number
     */
    totalUnknown: function () {
      return (
        this.pagination.total == undefined || this.pagination.total == null
      );
    },
    total: function () {
      return this.pagination.total;
    },
    displayTotal: function () {
      return this.pagination.displayCount || this.pagination.total;
    },
    totalPages: function () {
      return Math.ceil(this.pagination.totalAvailable / this.pagination.size);
    },
    selectedSize: function () {
      // Ensure we always return a string value for the selectedSize
      const size = this.setPaginationSize();
      return size ? size.toString() : "";
    },
    displaySizes: function () {
      return this.pagination.sizes;
    },
    hasNext: function () {
      return this.pagination.hasNext;
    },
    pageSizes: function () {
      const sizes = this.pagination.sizes;
      const newSizes = Array.isArray(sizes) ? sizes : sizes.split(",");
      return newSizes.map((el) => {
        return {
          text: el,
          value: el
        };
      });
    }
  },
  methods: {
    setPaginationSize: function () {
      // Check if pagination.size is in pagination.sizes or if it's undefined/null
      const sizeIsUndefinedOrNull =
        this.pagination.size === undefined || this.pagination.size === null;
      const exists = () =>
        this.pageSizes.some((item) => item.value == this.pagination.size);

      if (
        this.pageSizes &&
        this.pageSizes.length &&
        (sizeIsUndefinedOrNull || !exists())
      ) {
        // Set pagination.size to the lowest value in pagination.sizes
        const values = this.pageSizes.map((item) => Number(item.value));

        return Math.min(...values).toString();
      }

      return this.pagination.size;
    },
    pageFirst: function (event, sourceId) {
      // console.log("ui-repeater-pagination-update pageFirst firing");
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        sourceId: sourceId,
        page: 1,
        size: this.pagination.size,
        action: "first"
      });
    },
    pageLast: function (event, sourceId) {
      // console.log("ui-repeater-pagination-update pageLast firing");
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        sourceId: sourceId,
        page: this.totalPages,
        size: this.pagination.size,
        action: "last"
      });
    },
    pagePrev: function (event, sourceId) {
      // console.log("ui-repeater-pagination-update pagePrev firing");
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        sourceId: sourceId,
        page: this.pagination.page - 1,
        size: this.pagination.size,
        action: "previous"
      });
    },
    pageNext: function (event, sourceId) {
      // console.log("ui-repeater-pagination-update pageNext firing");
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        sourceId: sourceId,
        page: this.pagination.page + 1,
        size: this.pagination.size,
        action: "next"
      });
    },
    pageSelect: function (event, page, sourceId) {
      // console.log("ui-repeater-pagination-update pageSelect firing");
      var pageNum = parseInt(page, 10);

      // If the nan, it must be an empty value (deleted) since the input masked to just numbers
      // We just need to refresh the input field to the current selected page
      if (isNaN(pageNum)) {
        // Make sure the input field rendering is refreshed when the page selection changes to any value
        this.pageInputKey =
          this.pageInputKey === -1 ? this.selectedPageFromInput : -1;
        this.stopProcessUpdate(
          "catPagination.vue name: cat-pagination method: pageSelect-isNaN"
        );
        return;
      }

      this.pageInputKey = pageNum;
      if (pageNum !== this.pagination.page) {
        this.$emit("ui-repeater-pagination-update", {
          event: event,
          id: this.id,
          page: pageNum,
          size: this.pagination.size,
          action: "select",
          sourceId: sourceId
        });
      } else {
        if (Catalyst?.debug) {
          console.debug(
            "Catalyst.processing=false (glue-completed-filing-detail-clickPrevious)"
          );
        }
        Catalyst.processing = false;
      }
    },
    pageSize: function (event, sourceId) {
      // console.log("ui-repeater-pagination-update pageSize firing");
      this.$emit("ui-repeater-pagination-update", {
        event: event,
        id: this.id,
        sourceId: sourceId,
        page: 1,
        size: event.value,
        action: "size"
      });
    },
    pageLabel: function (page) {
      return this.resolveTxt("skin.pagination.page", "Page") + " " + page;
    }
  }
});
</script>
